import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getProfile } from '../services/api';
import { ROLES, getStoredUser, getUserRole } from '../hooks/usePermissions';

const AuthContext = createContext();

/**
 * Live auth/permissions for the signed-in user.
 *
 * The role is the single source of truth for what the UI exposes. We seed it
 * from the localStorage snapshot for an instant first paint, then refresh it
 * from `/auth/me` on mount (and after login / company switch) so a stale cached
 * profile can never strand a user on the wrong role.
 */
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
};

// Back-compat alias: components historically called this "permissions".
export const usePermissions = useAuth;

const isAuthenticated = () =>
    localStorage.getItem('isAuthenticated') === 'true' &&
    Boolean(localStorage.getItem('token'));

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => getStoredUser());
    const [loading, setLoading] = useState(true);

    // Pull the authoritative profile (incl. role) from the server and cache it.
    const refresh = useCallback(async () => {
        if (!isAuthenticated()) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const res = await getProfile();
            const profile = res.data;
            localStorage.setItem('user', JSON.stringify(profile));
            setUser(profile);
            return profile;
        } catch {
            // Network/401 — keep whatever we already had rather than crashing.
            return getStoredUser();
        } finally {
            setLoading(false);
        }
    }, []);

    // Let callers (e.g. company switch) push a fresh profile they already have.
    const setProfile = useCallback((profile) => {
        if (!profile) return;
        localStorage.setItem('user', JSON.stringify(profile));
        setUser(profile);
    }, []);

    const clear = useCallback(() => {
        localStorage.removeItem('user');
        setUser(null);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const role = getUserRole(user);
    const value = {
        user,
        role,
        loading,
        refresh,
        setProfile,
        clear,
        isAdmin: role === ROLES.ADMIN,
        isEditor: role === ROLES.EDITOR,
        isViewer: role === ROLES.VIEWER,
        // Editor + admin may create/run/mutate; viewers are read-only.
        canWrite: role !== ROLES.VIEWER,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
